import type { ResellerEntitlement } from './commerce';
import type { PartnerGrantRecord } from './partner-links';
import type { SalesWorkspaceContext } from './sales-collaboration';

export interface SalesMapContextOption {
  id: string;
  label: string;
  kind: 'self' | 'linked-seller';
  catalog_owner_tenant_id: string;
  entitlement_id?: string | null;
  request_enabled: boolean;
}

function normalize(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function buildSalesMapContextOptions(args: {
  activeTenantId: string;
  activeTenantName?: string | null;
  linkedSellers: PartnerGrantRecord[];
  entitlements: ResellerEntitlement[];
}): SalesMapContextOption[] {
  const activeTenantId = normalize(args.activeTenantId);
  if (!activeTenantId) {
    return [];
  }

  const options = new Map<string, SalesMapContextOption>();
  const entitlementsByOwner = new Map<string, ResellerEntitlement[]>();

  for (const item of args.entitlements) {
    const ownerTenantId = normalize(item.catalog_owner_tenant_id);
    if (!ownerTenantId) {
      continue;
    }
    const records = entitlementsByOwner.get(ownerTenantId) ?? [];
    records.push(item);
    entitlementsByOwner.set(ownerTenantId, records);
  }

  const buildOption = (
    ownerTenantId: string,
    label: string,
    kind: SalesMapContextOption['kind'],
  ): SalesMapContextOption => {
    const matchingEntitlements = entitlementsByOwner.get(ownerTenantId) ?? [];
    return {
      id: `${kind}:${ownerTenantId}`,
      label,
      kind,
      catalog_owner_tenant_id: ownerTenantId,
      entitlement_id: matchingEntitlements.length === 1 ? matchingEntitlements[0]?.entitlement_id ?? null : null,
      request_enabled: matchingEntitlements.length > 0,
    };
  };

  options.set(
    activeTenantId,
    buildOption(activeTenantId, normalize(args.activeTenantName) || activeTenantId, 'self'),
  );

  for (const item of args.linkedSellers) {
    const ownerTenantId = normalize(item.grantingTenantId);
    if (!ownerTenantId) {
      continue;
    }
    const label = normalize(item.grantingTenantName) || normalize(item.grantingTenantSlug) || ownerTenantId;
    options.set(ownerTenantId, buildOption(ownerTenantId, label, ownerTenantId === activeTenantId ? 'self' : 'linked-seller'));
  }

  for (const item of args.entitlements) {
    const ownerTenantId = normalize(item.catalog_owner_tenant_id);
    if (!ownerTenantId || options.has(ownerTenantId)) {
      continue;
    }
    const label = normalize(item.name) || ownerTenantId;
    options.set(ownerTenantId, buildOption(ownerTenantId, label, ownerTenantId === activeTenantId ? 'self' : 'linked-seller'));
  }

  return Array.from(options.values()).sort((left, right) => {
    if (left.catalog_owner_tenant_id === activeTenantId && right.catalog_owner_tenant_id !== activeTenantId) {
      return -1;
    }
    if (right.catalog_owner_tenant_id === activeTenantId && left.catalog_owner_tenant_id !== activeTenantId) {
      return 1;
    }
    return left.label.localeCompare(right.label);
  });
}

export function selectDefaultSalesMapContext(options: SalesMapContextOption[]): SalesMapContextOption | null {
  if (options.length === 0) {
    return null;
  }
  const linkedSellerOptions = options.filter(option => option.kind === 'linked-seller');
  if (linkedSellerOptions.length > 0) {
    const requestEnabledOption = linkedSellerOptions.find(option => option.request_enabled);
    return requestEnabledOption ?? linkedSellerOptions[0];
  }
  return options[0];
}

export function toSalesWorkspaceContext(option: SalesMapContextOption | null): SalesWorkspaceContext | null {
  if (!option) {
    return null;
  }
  return {
    catalog_owner_tenant_id: option.catalog_owner_tenant_id,
    entitlement_id: option.entitlement_id ?? null,
  };
}
